import { defineStore } from 'pinia'
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useActiveDeviceRenderStore } from "@/stores/activeDeviceRender";
import linkifyHtml from "linkify-html";
import categoryMapping from "@/data/categoryMapping";
import categoryGrouping from "@/data/categoryGrouping";

export const useIssuesStore = defineStore('issues', () => {
    const activeDeviceRender = useActiveDeviceRenderStore();
    const route = useRoute();
    const router = useRouter();

    function truncate(value, length = 36) {
        if (value.length >= length) {
            return value.substring(0, length) + '...'
        }
        return value

    }

    function processOccurrences(issueType, callbackFunction = null) {
        let result = []
        for (let deviceRender of activeDeviceRender.deviceRenders) {
            if (deviceRender[issueType]) {
                for (let entry of deviceRender[issueType]) {
                    let existing = result.find((item) => {
                        return item.id === entry.id
                    });

                    if (existing) {
                        if (!existing['devices'].map(d => d.name).includes(deviceRender.name)) {
                            existing['devices'].push({
                                name: deviceRender.name,
                                renderId: deviceRender.id,
                                occurrences: entry.occurrences,
                                id: deviceRender.deviceId,
                            })
                        }
                        if (deviceRender.deviceId === activeDeviceRender.deviceRender.deviceId) {
                            existing.occurrences = JSON.parse(JSON.stringify(entry.occurrences));
                            existing.priority = 'high';
                            if (callbackFunction) {
                                callbackFunction(entry, existing);
                            }
                        }
                    } else {
                        let box = {}
                        Object.assign(box, entry.occurrences[0].box);
                        let newItem = {
                            id: entry.id,
                            reason: entry.reason,
                            ignored: entry.ignored,
                            occurrences: deviceRender.deviceId === activeDeviceRender.deviceRender.deviceId ? entry.occurrences : [],
                            devices: [{
                                name: deviceRender.name,
                                renderId: deviceRender.id,
                                occurrences: entry.occurrences,
                                id: deviceRender.deviceId,
                            }]
                        }
                        newItem = JSON.parse(JSON.stringify(newItem))

                        if (callbackFunction) {
                            callbackFunction(entry, newItem);
                        }

                        result.push(newItem)
                    }
                }
            }
        }
        return result
    }

    const brokenLinkMarks = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let entries = processOccurrences('brokenLinkMarks', function (entry, newItem) {
            newItem.query = 'broken_link';
            newItem.category = 'technical.broken_link';

            newItem.priority = entry.priority || 'high';
            newItem.showStatusCode = true;
            newItem.statusCode = `${entry.status}`;
            newItem.name = `${entry.occurrences[0].text}`;
            newItem.description = entry.url;
            newItem.url = entry.url;

            for (let occurrence of newItem.occurrences) {
                occurrence.highlightDetails = {}

                occurrence.highlightDetails.title = `Broken Link Detected`;
                occurrence.highlightDetails.text_1 = linkifyHtml(entry.url, {
                    defaultProtocol: 'https',
                    className: 'external-link-plain',
                    target: {
                        url: '_blank'
                    }
                });
                occurrence.highlightDetails.context = occurrence.context;
                occurrence.highlightDetails.showContext = !occurrence.visible || (['title', 'alt'].includes(occurrence.contextType));

                if (occurrence.box && occurrence.box.height > 500) {
                    occurrence.box.height = 50;
                }

            }

        });
        entries = entries.filter((e) => !e['ignored'])
        entries.sort((a, b) => {
            if (!a.occurrences[0] || !b.occurrences[0]) {
                return 0;
            }
            // First, sort by 'visible' (true values first)
            let occurrencesA = a.occurrences[0];
            let occurrencesB = b.occurrences[0];

            if (occurrencesA.visible && !occurrencesB.visible) return -1;
            if (!occurrencesA.visible && occurrencesB.visible) return 1;

            if (occurrencesA.box.top < occurrencesB.box.top) return -1;
            if (occurrencesA.box.top > occurrencesB.box.top) return 1;

            if (occurrencesA.box.left < occurrencesB.box.left) return -1;
            if (occurrencesA.box.left > occurrencesB.box.left) return 1;

            return 0; // If all criteria are equal
        });
        return entries

    })

    const technicalMissingTitles = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let result = []

        if (activeDeviceRender.page.seoIssueMissingTitle) {
            result.push({
                id: 'seo_issue_missing_title',
                query: 'seo_error',
                category: 'technical.missing_title',
                name: 'Page titles are crucial for usability and SEO.',
                description: '',
                occurrences: [],
                devices: [],
                priority: 'high',
            })
        }

        return result

    })

    const contentErrorMissingFavicons = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let result = []
        if (activeDeviceRender.page.contentIssueMissingFavicon) {
            result.push({
                id: 'content_issue_missing_favicon',
                query: 'content_error',
                category: 'technical.missing_favicon',
                name: 'Favicon images are important for branding and usability.',
                description: 'Favicon images are important for branding and usability.',
                occurrences: [],
                devices: [],
                priority: 'high',
            })
        }

        return result

    })
    const technicalMissingDescriptions = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let result = []

        if (activeDeviceRender.page.seoIssueMissingDescription) {
            result.push({
                id: 'seo_issue_missing_description',
                query: 'seo_warning',
                category: 'technical.missing_description',
                name: 'Meta descriptions are important for usability and SEO.',
                description: 'Fix: add <meta name="description" content=".."/> to your page.',
                occurrences: [],
                devices: [],
                priority: 'medium',
            })
        }


        return result

    })
    const layoutErrors = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        return processOccurrences('layoutErrors', function (entry, newItem) {

            newItem.query = entry.reason;
            newItem.category = 'layout.' + entry.reason;
            newItem.priority = entry.priority || 'high';

            let reason = entry.reason;
            let description = categoryMapping.layout[reason].info;
            let errorName = categoryMapping.layout[reason].label;

            if (reason === 'entity') {
                errorName = 'Character Escaping Error'
                description = `"${entry.occurrences[0].word}" was double escaped, did you mean "${entry.occurrences[0].suggestion}".`
            }
            if (['overflowing_container'].includes(reason)) {
                errorName = 'Overflowing Container'
                description = `"${entry.occurrences[0].text}" is overflowing its container.`
            }

            if (reason === 'outside_window') {
                errorName = 'Text flows outside of window'
                description = entry.occurrences[0].text

            }
            if (reason === 'button_overlap') {
                errorName = 'Overlapping Buttons Detected'
                description = `The button "${truncate(entry.occurrences[0].text, 35)}" overlaps with another button`
            }
            newItem.name = errorName;
            newItem.description = description;
            for (let occurrence of newItem.occurrences) {
                occurrence.highlightDetails = {}

                occurrence.highlightDetails.title = errorName;
                occurrence.highlightDetails.text_1 = description;
                if (reason === 'horizontal_scrolling') {
                    occurrence.highlightDetails.text_2 = 'The element below is the most likely cause.'
                }
                if (reason === 'aspect_ratio') {
                    occurrence.highlightDetails.text_2 = `<a title="External Link" target="_blank" href="${occurrence.url}" class="external-link overflow-text-ellipsis-inline">${occurrence.url}</a>`;
                }
                occurrence.highlightDetails.showContext = false;


                if (['outside_window', 'unsupported_unicode'].includes(reason)) {
                    occurrence.box.width += 4;
                    occurrence.box.height += 4;
                    occurrence.box.left -= 2;
                    occurrence.box.top -= 2;
                }
                if (['button_overlap', 'text_overlap'].includes(reason)) {
                    occurrence.box.top = Math.min(occurrence.box.top, occurrence.box2.top)
                    occurrence.box.left = Math.min(occurrence.box.left, occurrence.box2.left)
                    occurrence.box.height = Math.max(occurrence.box.top + occurrence.box.height, occurrence.box2.top + occurrence.box2.height) - occurrence.box.top
                    occurrence.box.width = Math.max(occurrence.box.left + occurrence.box.width, occurrence.box2.left + occurrence.box2.width) - occurrence.box.left
                }
                if ((['horizontal_scrolling'].includes(reason) || occurrence.highlightDetails.showContext) && occurrence.box.height > 500) {
                    occurrence.box.height = 50;
                }

            }

        });

    })

    const consoleErrors = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let results = []
        for (let consoleLog of activeDeviceRender.page.consoleLogIssues) {
            if (!['error', 'pageerror'].includes(consoleLog.type)) {
                continue;
            }
            if (consoleLog.ignored) {
                continue
            }
            results.push({
                id: consoleLog.id || consoleLog.stack || consoleLog.text,
                name: 'console_error',
                query: 'console_error',
                category: 'technical.console_error',
                classes: ['text-danger', 'bg-danger-subtle', 'rounded', 'issue-item'],
                text: consoleLog.stack || consoleLog.text,
                occurrences: consoleLog.occurrences || [],
                type: consoleLog.type,
                priority: 'medium',
            })
        }
        return results;
    })

    const brokenResources = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let results = []
        for (let brokenResource of activeDeviceRender.page.brokenResourceIssues) {
            if (brokenResource.ignored) {
                continue
            }
            results.push({
                id: brokenResource.id || brokenResource.url,
                name: 'broken_resource',
                query: 'broken_resource',
                category: 'technical.broken_resource',
                classes: ['text-danger', 'bg-warning-subtle', 'rounded', 'issue-item'],
                url: brokenResource.url,
                status: brokenResource.status,
                occurrences: brokenResource.occurrences || [],
                type: brokenResource.type,
                priority: 'low'
            })
        }
        return results;
    })

    const internalRedirectMarks = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let entries = processOccurrences('internalRedirectMarks', function (entry, newItem) {
            newItem.query = 'internal_redirect';
            newItem.category = 'technical.internal_redirect';
            newItem.priority = entry.priority || 'low';

            newItem.name = `${entry.url}`;
            newItem.description = `Redirected to: ${entry.redirected_to}`;
            newItem.url = entry.url;

            for (let occurrence of newItem.occurrences) {
                occurrence.highlightDetails = {}

                occurrence.highlightDetails.title = `Internal Redirect Detected`;
                occurrence.highlightDetails.text_1 = linkifyHtml(entry.url, {
                    defaultProtocol: 'https',
                    className: 'external-link',
                    target: {
                        url: '_blank'
                    }
                });
                occurrence.highlightDetails.text_2 = 'Redirected to: ' + linkifyHtml(entry.redirected_to, {
                    defaultProtocol: 'https',
                    className: 'external-link',
                    target: {
                        url: '_blank'
                    }
                });
                occurrence.highlightDetails.context = occurrence.context;
                occurrence.highlightDetails.showContext = !occurrence.visible || (['title', 'alt'].includes(occurrence.contextType));

                if (occurrence.box && occurrence.box.height > 500) {
                    occurrence.box.height = 50;
                }

            }


        });
        entries = entries.filter((e) => !e['ignored'])

        entries.sort((a, b) => {
            if (!a.occurrences[0] || !b.occurrences[0]) {
                return 0;
            }
            // First, sort by 'visible' (true values first)
            let occurrencesA = a.occurrences[0];
            let occurrencesB = b.occurrences[0];

            if (!(occurrencesA && occurrencesB)) {
                return 0;
            }

            if (occurrencesA.visible && !occurrencesB.visible) return -1;
            if (!occurrencesA.visible && occurrencesB.visible) return 1;

            if (occurrencesA.box.top < occurrencesB.box.top) return -1;
            if (occurrencesA.box.top > occurrencesB.box.top) return 1;

            if (occurrencesA.box.left < occurrencesB.box.left) return -1;
            if (occurrencesA.box.left > occurrencesB.box.left) return 1;

            return 0; // If all criteria are equal
        });
        return entries

    })

    const missingAltTagImageMarks = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let entries = processOccurrences('missingAltTagImageMarks', function (entry, newItem) {
            newItem.query = entry.reason;
            newItem.category = 'technical.missing_alt';

            newItem.priority = entry.priority || 'medium';
            newItem.name = '';
            newItem.description = 'Images with missing alt tags detected.';
            newItem.url = entry.url;

            for (let occurrence of newItem.occurrences) {
                occurrence.highlightDetails = {}

                occurrence.highlightDetails.title = `Missing Alt Tag Detected`;
                occurrence.highlightDetails.text_1 = 'The image below is missing an alt tag'
                occurrence.highlightDetails.context = occurrence.context;
                occurrence.highlightDetails.showContext = false;

                if (occurrence.box && occurrence.box.height > 500) {
                    occurrence.box.height = 50;
                }

            }

        });
        entries = entries.filter((e) => !e['ignored'])
        for (let entry of entries) {
            entry.occurrences.sort((a, b) => {
                if (a.box.top > b.box.top) {
                    return 1
                }
                if (a.box.top < b.box.top) {
                    return -1
                }
                return 0
            })
        }
        entries.sort((a, b) => {
            if (!a.occurrences[0] || !b.occurrences[0]) {
                return 0;
            }
            // First, sort by 'visible' (true values first)
            let occurrencesA = a.occurrences[0];
            let occurrencesB = b.occurrences[0];

            if (!occurrencesA) {
                return -1;
            }
            if (!occurrencesB) {
                return 1;
            }

            if (occurrencesA.visible && !occurrencesB.visible) return -1;
            if (!occurrencesA.visible && occurrencesB.visible) return 1;

            if (occurrencesA.box.top < occurrencesB.box.top) return -1;
            if (occurrencesA.box.top > occurrencesB.box.top) return 1;

            if (occurrencesA.box.left < occurrencesB.box.left) return -1;
            if (occurrencesA.box.left > occurrencesB.box.left) return 1;

            return 0; // If all criteria are equal
        });
        return entries

    })

    const brokenImageMarks = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let entries = processOccurrences('brokenImageMarks', function (entry, newItem) {
            newItem.query = 'broken_image';
            newItem.category = 'technical.broken_image';

            newItem.priority = entry.priority || 'high';
            newItem.name = '';
            newItem.description = entry.url;
            newItem.url = entry.url;

            for (let occurrence of newItem.occurrences) {
                occurrence.highlightDetails = {}

                occurrence.highlightDetails.title = `Broken Image Detected`;
                occurrence.highlightDetails.text_1 = linkifyHtml(entry.url, {
                    defaultProtocol: 'https',
                    className: 'external-link',
                    target: {
                        url: '_blank'
                    }
                });
                occurrence.highlightDetails.context = occurrence.context;
                occurrence.highlightDetails.showContext = false;

                if (occurrence.box && occurrence.box.height > 500) {
                    occurrence.box.height = 50;
                }

            }

        });
        entries = entries.filter((e) => !e['ignored'])
        entries.sort((a, b) => {
            if (!a.occurrences[0] || !b.occurrences[0]) {
                return 0;
            }
            // First, sort by 'visible' (true values first)
            let occurrencesA = a.occurrences[0];
            let occurrencesB = b.occurrences[0];

            if (!occurrencesA) {
                return -1;
            }
            if (!occurrencesB) {
                return 1;
            }

            if (occurrencesA.visible && !occurrencesB.visible) return -1;
            if (!occurrencesA.visible && occurrencesB.visible) return 1;

            if (occurrencesA.box.top < occurrencesB.box.top) return -1;
            if (occurrencesA.box.top > occurrencesB.box.top) return 1;

            if (occurrencesA.box.left < occurrencesB.box.left) return -1;
            if (occurrencesA.box.left > occurrencesB.box.left) return 1;

            return 0; // If all criteria are equal
        });
        return entries

    })

    const crawlErrors = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        let result = [];
        for (let deviceRender of activeDeviceRender.deviceRenders) {
            if (deviceRender.crawlErrors) {
                result = result.concat(deviceRender.crawlErrors);
            }
        }
        return result;
    })


    const cloudFlareError = computed(() => {
        // force reactivity (this is so that highlightdetails will be recalculated)
        route.query

        return !!crawlErrors.value.find((error) => {
            return error.reason === 'cloudflare'
        });


    })

    const allAuditIssues = computed(() => {
        return [
            ...brokenLinkMarks.value,
            ...internalRedirectMarks.value,
            ...brokenImageMarks.value,
            ...missingAltTagImageMarks.value,
            ...brokenResources.value,
            ...consoleErrors.value,
            ...layoutErrors.value,
            ...contentErrorMissingFavicons.value,
            ...technicalMissingTitles.value,
            ...technicalMissingDescriptions.value,
        ]
    })


    function getIssueFromQuery(query) {

        if (query.focus) {
            const [cat, detail] = query.focus.split(/\.(.+)/);
            let innerCategories = null;
            if (!detail){
                innerCategories = categoryGrouping[cat].innerCategories;
            }
            let v = allAuditIssues.value.filter((issue) => {
                if (query.focus_priority && issue.priority !== query.focus_priority) {
                    return false;
                }
                if (innerCategories){
                    return innerCategories.includes(issue.category)
                }
                else if (issue.category === query.focus){
                    return true;
                }
                return false;
            });
            if (v.length > 0) {
                return v[0];
            }
        }

        let layoutCategories = Object.keys(categoryMapping.layout);
        let foundKey = layoutCategories.find(key => key in query);
        if (!foundKey){
            let technicalCategories = Object.keys(categoryMapping.technical);
            foundKey = technicalCategories.find(key => key in query);
        }

        if (foundKey) {
            let broadMatches = allAuditIssues.value.filter((layoutError) => {
                return layoutError.category.split('.')[1] === foundKey;
            })
            let exactMatches = broadMatches.filter((layoutError) => {
                return layoutError.id === query[foundKey];
            })
            if (exactMatches.length > 0) {
                return exactMatches[0];
            }
            else if (broadMatches.length > 0) {
                return broadMatches[0];
            }
        }
        return null
    }

    return {
        cloudFlareError,
        consoleErrors,
        crawlErrors,
        brokenImageMarks,
        missingAltTagImageMarks,
        internalRedirectMarks,
        brokenResources,
        brokenLinkMarks,
        technicalMissingTitles,
        contentErrorMissingFavicons,
        layoutErrors,
        technicalMissingDescriptions,
        allAuditIssues,
        getIssueFromQuery
    }
})